import _ from "lodash";
import { NewsDTO } from "../domain/models/dto/NewsDTO";
import {
    INews,
    NewsFiltersValues,
    NewsFlatCollection,
} from "../domain/models/News";
import { NewsService } from "../domain/services/NewsService";
import { NewsMapper } from "../mappers/NewsMapper";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";

export class NewsServiceImpl extends BaseServiceImpl implements NewsService {
    static _instance: NewsService;
    private newsMapper = new NewsMapper();
    private readonly newsEndpoint = "/api/v1/news";

    private constructor() {
        super();
    }

    static getInstance(): NewsService {
        if (!NewsServiceImpl._instance) {
            NewsServiceImpl._instance = new NewsServiceImpl();
        }
        return NewsServiceImpl._instance;
    }

    instantiate(entity: INews): INews {
        return this.newsMapper.fromDTO(entity);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: NewsFiltersValues = {} as NewsFiltersValues
    ): Promise<CollectionResponse<INews>> {
        try {
            let url = `${this.newsEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (key && !_.isNil(value) && !_.isEmpty(value)) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<NewsFlatCollection>(url);

            return {
                ...result,
                rows: result.rows.map((dto) => this.newsMapper.fromDTO(dto)),
            };
        } catch (err) {
            console.log("Error in NewsServiceImpl.getAllNews");
            console.log(err);
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: INews): Promise<INews> {
        try {
            const encodedDto = this.newsMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(`${this.newsEndpoint}/${dto.id}`, encodedDto);
            }

            return this.post(this.newsEndpoint, encodedDto);
        } catch (err) {
            console.log("Error in NewsServiceImpl.createOrUpdateNews");
            console.log(err);
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.newsEndpoint}/${id}`);
        } catch (err) {
            console.log("Error in NewsServiceImpl.deleteById");
            console.log(err);
        }
    }

    async getById(id: string): Promise<INews | null> {
        try {
            if (!id) return null;

            const dto = await this.get<NewsDTO>(
                `${this.newsEndpoint}/${id}?getEntityTranslations=true`
            );
            return this.newsMapper.fromDTO(dto);
        } catch (err) {
            console.log("Error in NewsServiceImpl.getNewsById");
            console.log(err);
            return null;
        }
    }
}

export function newsServiceFactory(): NewsService {
    return NewsServiceImpl.getInstance();
}
